import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import RelatedArticles from "../components/RelatedArticles";
import { api } from "../lib/api";
import { ArrowUpRight } from "lucide-react";

export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [notFound, setNotFound] = useState(false);
    useEffect(() => {
        api.get(`/blog/posts/${slug}`).then(({ data }) => setPost(data)).catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) return (
        <div className="max-w-4xl mx-auto px-6 py-24 text-center" data-testid="blog-post-not-found">
            <h1 className="font-serif text-5xl text-forest">Not found.</h1>
            <Link to="/blog" className="btn-pill mt-8 inline-flex">Back to Blog</Link>
        </div>
    );

    if (!post) return <div className="min-h-[60vh] flex items-center justify-center font-mono text-xs uppercase text-stone">Loading…</div>;

    const canonical = `https://thepuropack.com/blog/${post.slug}`;
    const faqs = Array.isArray(post.faqs) ? post.faqs.filter((f) => f && f.question && f.answer) : [];

    const graph = [
        {
            "@type": "BlogPosting",
            "@id": `${canonical}#post`,
            headline: post.title,
            description: post.excerpt,
            image: post.cover_image ? (post.cover_image.startsWith("http") ? post.cover_image : `https://thepuropack.com${post.cover_image}`) : undefined,
            author: { "@type": "Organization", name: post.author },
            datePublished: post.created_at,
            dateModified: post.updated_at || post.created_at,
            mainEntityOfPage: canonical,
            publisher: {
                "@type": "Organization",
                name: "The Puro Pack",
                logo: { "@type": "ImageObject", url: "https://thepuropack.com/logo-512.png" },
            },
        },
    ];
    if (faqs.length) {
        graph.push({
            "@type": "FAQPage",
            "@id": `${canonical}#faq`,
            mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.question,
                acceptedAnswer: { "@type": "Answer", text: f.answer },
            })),
        });
    }
    const schema = { "@context": "https://schema.org", "@graph": graph };

    return (
        <article data-testid={`blog-post-${post.slug}`}>
            <Seo title={post.meta_title || post.title} description={post.excerpt} path={`/blog/${post.slug}`} image={post.cover_image} schema={schema} type="article" />
            <div className="max-w-[1600px] mx-auto px-6 lg:px-10">
                <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Blog", to: "/blog" }, { label: post.title, to: `/blog/${post.slug}` }]} />
            </div>

            <header className="max-w-4xl mx-auto px-6 pt-12 pb-10">
                <div className="font-mono text-xs uppercase tracking-widest text-clay mb-6">{(post.tags || []).join(" · ")}</div>
                <h1 className="font-serif text-5xl lg:text-7xl leading-[1] tracking-tight text-forest">{post.title}</h1>
                <p className="mt-6 text-xl text-inkgreen/80 font-serif italic">{post.excerpt}</p>
                <div className="mt-8 font-mono text-xs uppercase tracking-widest text-stone">By {post.author} · {new Date(post.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>
            </header>

            {post.cover_image && (
                <div className="max-w-6xl mx-auto px-6 mb-12">
                    <div className="image-frame aspect-[16/9]">
                        <img src={post.cover_image} alt={post.title} />
                    </div>
                </div>
            )}

            <div className="max-w-3xl mx-auto px-6 pb-16 prose-editorial">
                <ReactMarkdown>{post.content}</ReactMarkdown>
            </div>

            {faqs.length > 0 && (
                <section className="max-w-3xl mx-auto px-6 pb-16" data-testid="blog-post-faq">
                    <div className="border-t border-forest/30 pt-10">
                        <div className="font-mono text-xs uppercase tracking-widest text-clay mb-6">[ Frequently Asked Questions ]</div>
                        <h2 className="font-serif text-3xl lg:text-4xl text-forest tracking-tight mb-8">Answered by our team</h2>
                        <div className="divide-y divide-forest/20">
                            {faqs.map((f, i) => (
                                <details key={i} className="py-5 group" data-testid={`faq-${i}`}>
                                    <summary className="cursor-pointer list-none flex justify-between items-start gap-6">
                                        <span className="font-serif text-xl text-forest">{f.question}</span>
                                        <span className="font-mono text-xs text-clay mt-1 group-open:rotate-45 transition-transform">+</span>
                                    </summary>
                                    <div className="mt-4 text-inkgreen/85 leading-relaxed">
                                        <ReactMarkdown>{f.answer}</ReactMarkdown>
                                    </div>
                                </details>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <RelatedArticles currentSlug={post.slug} tags={post.tags} />

            <section className="bg-forest text-bone mt-8">
                <div className="max-w-4xl mx-auto px-6 py-16 flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
                    <div>
                        <div className="font-mono text-xs uppercase tracking-widest text-clay mb-3">[ Talk to us ]</div>
                        <h2 className="font-serif text-3xl md:text-5xl tracking-tight max-w-2xl">Ready to switch to sustainable packaging?</h2>
                        <p className="mt-4 text-bone/80 max-w-xl">Bangalore-based manufacturer serving QSR, cloud kitchens, cafés and exporters across India, GCC, UK & EU.</p>
                    </div>
                    <Link to="/contact" className="btn-pill border-bone text-bone hover:bg-bone hover:text-forest" data-testid="blog-post-cta">
                        Request a Sample <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </article>
    );
}
